import React, { useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { useSEO } from "../utils/seo";

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useSEO({
    title: "Frequently Asked Questions - AI API Cost Monitoring | Alertaio",
    description: "Get answers to common questions about Alertaio's AI API cost monitoring, budget alerts, supported providers, security, and pricing. Learn how to prevent OpenAI overspending.",
    keywords: "AI API FAQ, OpenAI cost monitoring FAQ, API budget alerts, Alertaio questions, AI cost management help, API monitoring support",
    canonicalUrl: "https://alertaio.com/faq",
    type: "website"
  });

  const faqs = [
    {
      question: "How does Alertaio monitor my AI API costs?",
      answer: "Alertaio connects to your OpenAI API account using read-only access. We track usage metrics, API calls, token consumption, and costs in real-time. Our system continuously monitors your spending and sends instant alerts when you approach your budget limits. All data is encrypted and we never access your prompts or sensitive information.",
    },
    {
      question: "Does Alertaio have access to my API prompts or data?",
      answer: "No. Alertaio only reads usage and cost metrics from your API provider. We never access your prompts, responses, or any sensitive data you send through the API. Your privacy is fully protected and we comply with all major data protection regulations including GDPR and SOC 2.",
    },
    {
      question: "Which AI APIs does Alertaio support?",
      answer: "Alertaio currently supports OpenAI (ChatGPT, GPT-4, GPT-3.5 Turbo, GPT-4 Turbo, DALL-E, and Whisper). We're continuously working to add support for Anthropic Claude, Google Gemini, Cohere, and other major LLM providers. If you need a specific provider, contact us and we'll prioritize it in our roadmap.",
    },
    {
      question: "How quickly will I receive cost alerts?",
      answer: "Alerts are sent in real-time, typically within 30-60 seconds of crossing your budget threshold. You can configure alerts via email, Slack, Discord, Microsoft Teams, or webhook. We also support SMS alerts for critical budget overruns.",
    },
    {
      question: "Can I set different budgets for different projects or API keys?",
      answer: "Yes! Alertaio allows you to create unlimited budget rules. Set different limits for production vs. development, by team, by project, or by individual API key. Each budget can have its own alert thresholds and notification channels.",
    },
    {
      question: "Is there a free plan or trial available?",
      answer: "Yes! We offer a 14-day free trial with full access to all features. No credit card required. After the trial, our Starter plan begins at $9/month for monitoring up to $500 in monthly AI API spend.",
    },
    {
      question: "What happens if I exceed my budget limit?",
      answer: "You receive an immediate alert when approaching your limit (customizable at 50%, 75%, 90%). If you exceed the limit, you're notified instantly. Optionally, you can enable automatic API key suspension to completely prevent overspending (available on Pro and Enterprise plans).",
    },
    {
      question: "Do I need technical knowledge to use Alertaio?",
      answer: "Not at all. Alertaio is designed for both developers and non-technical users. Our simple dashboard shows your costs in plain language. Setup takes less than 5 minutes—just connect your API key, set a budget, and you're done. No coding required.",
    },
    {
      question: "How does Alertaio help prevent unexpected OpenAI bills?",
      answer: "Alertaio provides real-time monitoring of your OpenAI API usage with customizable alert thresholds. You'll receive instant notifications before hitting your budget limits, allowing you to take action before costs spiral out of control. Our historical tracking also helps identify usage patterns and anomalies.",
    },
    {
      question: "Can Alertaio automatically stop my API if costs exceed a certain amount?",
      answer: "Yes, with our Pro and Enterprise plans, you can enable automatic API key suspension when your spending reaches a specified limit. This feature completely prevents overspending by temporarily disabling API access until you manually re-enable it or increase your budget.",
    },
    {
      question: "What notification channels does Alertaio support?",
      answer: "Alertaio supports multiple notification channels including email, Slack, Discord, Microsoft Teams, webhooks for custom integrations, and SMS for critical alerts. You can configure different channels for different alert types and budget thresholds.",
    },
    {
      question: "How does Alertaio compare to native OpenAI usage monitoring?",
      answer: "While OpenAI provides basic usage dashboards, Alertaio offers real-time alerts, multi-project budget management, team collaboration features, historical trend analysis, cost forecasting, and integration with multiple AI providers in one platform. We provide proactive monitoring rather than reactive checking.",
    },
    {
      question: "Is my data secure with Alertaio?",
      answer: "Absolutely. We use bank-level AES-256 encryption for data at rest and TLS/SSL for data in transit. We're SOC 2 compliant and undergo regular security audits. We only access billing and usage metrics—never your API prompts, responses, or application data.",
    },
    {
      question: "Can I monitor multiple AI API providers in one dashboard?",
      answer: "Currently, Alertaio focuses on OpenAI monitoring with best-in-class features and accuracy. We're actively developing support for multiple providers including Anthropic Claude, Google Gemini, and others. Soon you'll be able to get a unified view of all your AI spending across different providers in one dashboard.",
    },
    {
      question: "What happens if Alertaio's service goes down?",
      answer: "We maintain 99.9% uptime SLA with redundant infrastructure across multiple availability zones. In the unlikely event of an outage, your AI APIs continue working normally—Alertaio only monitors costs and doesn't sit in the request path. We also maintain fallback notification systems.",
    },
    {
      question: "How accurate is Alertaio's cost tracking?",
      answer: "Alertaio's cost tracking is highly accurate, typically within 1% of your actual bill. We sync with provider APIs every 5-10 minutes and use the same pricing models as the providers. Any minor discrepancies are usually due to API reporting delays, which we account for in our calculations.",
    },
    {
      question: "Can I export my usage and cost data?",
      answer: "Yes, all plans include data export capabilities. You can export usage reports, cost breakdowns, and historical data in CSV, JSON, or PDF formats. Enterprise plans also include API access to programmatically retrieve your monitoring data.",
    },
    {
      question: "Does Alertaio support team collaboration?",
      answer: "Yes! You can invite team members with different permission levels—admin, editor, or viewer. Team members can configure alerts, view dashboards, and receive notifications based on their role. This is perfect for agencies managing multiple client projects or development teams.",
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <Navigation />
      
      <main className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-slate-600">
                Everything you need to know about Alertaio's AI API cost monitoring and budget alerts.
              </p>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4 mb-16">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow duration-300"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full text-left px-6 py-5 flex items-center justify-between focus:outline-none"
                    aria-expanded={openIndex === index}
                  >
                    <h2 className="text-lg font-semibold text-slate-900 pr-8">
                      {faq.question}
                    </h2>
                    <svg
                      className={`w-6 h-6 text-indigo-600 flex-shrink-0 transition-transform duration-300 ${
                        openIndex === index ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <div className="px-6 pb-5">
                      <p className="text-slate-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Still have questions CTA */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                Still have questions?
              </h2>
              <p className="text-slate-600 mb-6">
                Our support team is here to help you get the most out of Alertaio.
              </p>
              <a
                href="mailto:support@alertaio.com"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-8 py-3 rounded-full transition-all transform hover:scale-105 shadow-md"
              >
                Contact Support
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
